import React, { useEffect, useState, useMemo, useRef } from "react"
import { Link, useNavigate } from "react-router-dom"
import _ from "lodash"
import { DateTime } from "luxon"
import api from "../../../api/axiosInstance"

import { Button } from "primereact/button"
import { SplitButton } from "primereact/splitbutton"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Skeleton } from "primereact/skeleton"

import { GET } from "../../../config/constants"

import useSubscribeAllDocs from "../../../hooks/useSubscribeAllDocs"
import handleStatusMessage from "../../../helpers/handleStatusMessage"

export default function ProjectsAndDataOrders() {
  const [fetching, setFetching] = useState(true)
  const [projectMetadata, setProjectMetadata] = useState([])

  const [pages, setPages] = useState([])
  const [currentPageIndex, setCurrentPageIndex] = useState(0)

  const hookParams = {
    collection: "mrf_orders",
    filter: ["mrf_type", "==", "in_network"],
    limit: 5,
    startAfterDoc: pages[currentPageIndex - 1] || null,
  }

  const [loading, error, data, lastVisible] = useSubscribeAllDocs(hookParams)

  const navigate = useNavigate()

  useEffect(() => {
    if (lastVisible && pages[currentPageIndex] !== lastVisible) {
      const newPages = [...pages]
      newPages[currentPageIndex] = lastVisible
      setPages(newPages)
    }
  }, [lastVisible])

  const dataIdsKey = useMemo(() => JSON.stringify(data?.map(d => d.id) || []), [data])
  const prevIdsKeyRef = useRef(null)

  useEffect(() => {
    if (!data) return
    if (prevIdsKeyRef.current === dataIdsKey) return
    prevIdsKeyRef.current = dataIdsKey
    const getProjectMetadata = async () => {
      const projectMetadataList = await Promise.all(
        _.map(data, async ({ id: mrf_order_id }) => {
          try {
            const res = await api.post(`/${GET.PROJECT_METADATA_FROM_MRF_ORDER}`, { mrf_order_id })
            const fetchData = res.data.data
            fetchData.mrf_order_id = mrf_order_id
            return fetchData
          } catch (error) {
            return null
          }
        })
      )
      setProjectMetadata(projectMetadataList)
      setFetching(false)
    }
    getProjectMetadata()
  }, [data, dataIdsKey])

  const handleData = useMemo(() => {
    if (!data) return []
    return _.map(data, (row) => {
      const currentProjectMetadata =
        _.find(projectMetadata, { mrf_order_id: row.id }) || {}

      const handleActions = () => {
        const { data_order_id: docId, data_order_status: status } =
          currentProjectMetadata
        const items = [
          {
            label: "Find MRF to process",
            icon: "pi pi-search",
            command: () => navigate(`/data-order/edit/${docId}`),
          },
          {
            label: "Add MRF to process",
            icon: "pi pi-plus",
            command: () => navigate(`/data-order/edit/${docId}?screen=3`),
          },
        ]
        return (
          <SplitButton
            label="View Order"
            onClick={() => navigate(`/data-order/details/${docId}/${status}`)}
            model={items}
          />
        )
      }

      const handleMrfOrderName = (name) => {
        if (!name) return
        const maxLength = 24
        return name.length > maxLength
          ? name.substring(0, maxLength) + "..."
          : name
      }

      const modDate = row.timestamp_updated
        ? row.timestamp_updated.toDate()
        : null

      const isProjectMetaLoading = !currentProjectMetadata.project_name

      return {
        ...row,
        mrf_order_name: handleMrfOrderName(row.mrf_order_name),
        mrf_order_stage: handleStatusMessage(row.mrf_order_stage),
        mrf_order_status: handleStatusMessage(row.mrf_order_status),
        project_name: isProjectMetaLoading ? (
          <Skeleton width="7rem" height="1.2rem" />
        ) : (
          <Link to={`/project/details/${currentProjectMetadata.project_alias}`}>
            {currentProjectMetadata.project_name}
          </Link>
        ),
        updated: DateTime.fromJSDate(modDate).toFormat("F"),
        actions: handleActions(),
      }
    })
  }, [data, projectMetadata])

  return (
    <div>
      <h2 style={{ marginBottom: 5 }}>Data Orders</h2>
      <small style={{ display: "block" }}>Showing last entries.</small>

      <div style={{ margin: "8px 0 16px 0" }}>
        <Button
          onClick={() => navigate("/data-order/add")}
          style={{ marginRight: 15 }}
          className="btn-secondary"
        >
          Create New Data Order
        </Button>
        <Button onClick={() => navigate("/data-order/list")}>
          Manage Datasets
        </Button>
      </div>

      <DataTable
        value={handleData}
        responsiveLayout="scroll"
        loading={loading || fetching}
      >
        <Column field="project_name" header="Project Name" />
        <Column field="mrf_order_name" header="Data Order Name" />
        <Column field="mrf_order_stage" header="Stage" />
        <Column field="mrf_order_status" header="Status" />
        <Column field="updated" header="Last Order Date" sortable />
        <Column field="actions" header="Actions" style={{ width: 240 }} />
      </DataTable>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "1rem",
          marginTop: 16,
        }}
      >
        <Button
          label="Previous"
          icon="pi pi-angle-left"
          onClick={() => setCurrentPageIndex((i) => Math.max(i - 1, 0))}
          disabled={currentPageIndex === 0}
        />
        <Button
          label="Next"
          icon="pi pi-angle-right"
          iconPos="right"
          onClick={() => setCurrentPageIndex((i) => i + 1)}
          disabled={!lastVisible || data.length < hookParams.limit}
        />
      </div>

      <br />
    </div>
  )
}
